/**
 * DialogsEspecificacao - Dialogs para adicionar itens, produtos e serviços
 */
import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import { Search, Package, Wrench, Plus, Ship } from 'lucide-react';
import api from '../../lib/axios';
import { CurrencyInput } from '../ui/currency-input';

// Dialog para adicionar novo item ao checklist
export const DialogAddItem = ({
  open,
  onClose,
  novoItem,
  setNovoItem,
  onAdd,
  vesselModel,
  categorias
}) => {
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Plus className="h-5 w-5 text-blue-600" />
            Adicionar Item ao Checklist
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Descrição *</Label>
            <Input
              value={novoItem.descricao}
              onChange={(e) => setNovoItem({ ...novoItem, descricao: e.target.value })}
              placeholder="Ex: Motor de popa Mercury 150HP"
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Categoria</Label>
              <Select
                value={novoItem.categoria}
                onValueChange={(v) => setNovoItem({ ...novoItem, categoria: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {categorias.map(cat => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                  {!categorias.includes('Outros') && (
                    <SelectItem value="Outros">Outros</SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Tipo</Label>
              <Select
                value={novoItem.tipo_item}
                onValueChange={(v) => setNovoItem({ ...novoItem, tipo_item: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="especificacao">📐 Especificação</SelectItem>
                  <SelectItem value="produto">📦 Produto</SelectItem>
                  <SelectItem value="servico">🔧 Serviço</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          {vesselModel && (
            <div className="flex items-center gap-2 p-3 bg-blue-50 rounded-lg">
              <Checkbox
                checked={novoItem.adicionar_ao_padrao}
                onCheckedChange={(checked) => setNovoItem({ ...novoItem, adicionar_ao_padrao: checked })}
              />
              <Label className="text-sm">
                Adicionar ao checklist padrão do modelo "{vesselModel.nome}"
              </Label>
            </div>
          )}
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={onAdd}>Adicionar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Dialog para adicionar produto a um item
export const DialogAddProduto = ({
  open,
  onClose,
  onAdd,
  buscaProduto,
  setBuscaProduto,
  produtosBusca,
  onBuscar
}) => {
  useEffect(() => {
    if (buscaProduto.length >= 2) {
      const timer = setTimeout(() => onBuscar(buscaProduto), 300);
      return () => clearTimeout(timer);
    }
  }, [buscaProduto, onBuscar]);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package className="h-5 w-5 text-green-600" />
            Vincular Produto
          </DialogTitle>
          <DialogDescription>
            Busque um produto do estoque para vincular ao item
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              value={buscaProduto}
              onChange={(e) => setBuscaProduto(e.target.value)}
              placeholder="Buscar por nome ou SKU..."
              className="pl-9"
            />
          </div>
          
          <div className="max-h-64 overflow-y-auto space-y-2">
            {produtosBusca.length === 0 ? (
              <p className="text-center text-gray-500 py-4">
                {buscaProduto.length < 2 
                  ? 'Digite pelo menos 2 caracteres para buscar' 
                  : 'Nenhum produto encontrado'}
              </p>
            ) : (
              produtosBusca.map(produto => (
                <button
                  key={produto.id}
                  onClick={() => onAdd(produto)}
                  className="w-full flex items-center gap-3 p-3 border rounded-lg hover:bg-gray-50 transition-colors text-left"
                >
                  <Package className="h-8 w-8 text-green-600 bg-green-50 p-1.5 rounded" />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">{produto.nome}</p>
                    <p className="text-xs text-gray-500 truncate">
                      SKU: {produto.sku} • Estoque: {produto.estoque_total || 0} {produto.unidade_medida || 'UN'}
                    </p>
                  </div>
                  <Badge variant="outline" className="text-green-700 bg-green-50 shrink-0">
                    {(produto.custo_medio || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                  </Badge>
                </button>
              ))
            )}
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Dialog para adicionar serviço
export const DialogAddServico = ({
  open,
  onClose,
  onAdd,
  novoServico,
  setNovoServico,
  fornecedores
}) => {
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wrench className="h-5 w-5 text-orange-600" />
            Adicionar Serviço / Custo Manual
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Descrição *</Label>
            <Input
              value={novoServico.descricao}
              onChange={(e) => setNovoServico({ ...novoServico, descricao: e.target.value })}
              placeholder="Ex: Instalação elétrica, Pintura..."
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Valor (R$) *</Label>
              <CurrencyInput
                value={novoServico.valor || 0}
                onChange={(v) => setNovoServico({ ...novoServico, valor: v })}
                placeholder="0,00"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Fornecedor</Label>
              <Select
                value={novoServico.fornecedor_id || "none"}
                onValueChange={(v) => setNovoServico({ ...novoServico, fornecedor_id: v === "none" ? "" : v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Nenhum</SelectItem>
                  {fornecedores.map(f => (
                    <SelectItem key={f.id} value={f.id}>
                      {f.razao_social || f.nome}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div className="space-y-2">
            <Label>Observação</Label>
            <Input
              value={novoServico.observacao}
              onChange={(e) => setNovoServico({ ...novoServico, observacao: e.target.value })}
              placeholder="Detalhes adicionais..."
            />
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button 
            onClick={onAdd}
            disabled={!novoServico.descricao || !novoServico.valor}
          >
            Adicionar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Dialog para importar especificações de outro projeto ou outra embarcação
export const DialogImportEspecificacao = ({
  open,
  onClose,
  projects,
  selectedProject,
  selectedEmbarcacao,
  onSelectProject,
  onSelectEmbarcacao,
  onImport,
  loading,
  currentProjectId,
  currentEmbarcacaoIndex
}) => {
  // Verificar se é o projeto atual
  const isCurrentProject = selectedProject?.id === currentProjectId;
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Ship className="h-5 w-5 text-blue-600" />
            Importar Ficha de Especificação
          </DialogTitle>
          <DialogDescription>
            Selecione um projeto e uma embarcação para copiar a ficha de especificação
          </DialogDescription>
        </DialogHeader>
        
        <div className="grid grid-cols-2 gap-4 max-h-96 overflow-y-auto">
          {/* Lista de Projetos */}
          <div className="space-y-2">
            <Label className="text-xs text-gray-500">Projetos Disponíveis</Label>
            <div className="space-y-1">
              {projects.map(project => {
                const isCurrent = project.id === currentProjectId;
                return (
                  <button
                    key={project.id}
                    onClick={() => onSelectProject(project)}
                    className={`w-full p-2 rounded text-left text-sm transition-colors ${
                      selectedProject?.id === project.id
                        ? 'bg-blue-100 border-blue-300 border'
                        : 'bg-gray-50 hover:bg-gray-100 border border-transparent'
                    }`}
                  >
                    <p className="font-medium truncate">
                      {project.name}
                      {isCurrent && <span className="ml-2 text-xs text-blue-600">(atual)</span>}
                    </p>
                    <p className="text-xs text-gray-500">{project.client}</p>
                    <p className="text-xs text-gray-400">
                      {project.embarcacoes?.length || 0} embarcação(ões)
                    </p>
                  </button>
                );
              })}
              {projects.length === 0 && (
                <p className="text-center text-gray-500 py-4 text-sm">
                  Nenhum projeto com especificações disponível
                </p>
              )}
            </div>
          </div>
          
          {/* Lista de Embarcações */}
          <div className="space-y-2">
            <Label className="text-xs text-gray-500">Embarcações</Label>
            <div className="space-y-1">
              {selectedProject?.embarcacoes?.map((emb) => {
                const embIndex = emb.index !== undefined ? emb.index : 0;
                const hasSpecs = emb.checklist_preenchido?.length > 0;
                // Desabilitar a embarcação atual (não pode importar de si mesma)
                const isCurrentEmb = isCurrentProject && embIndex === currentEmbarcacaoIndex;
                const isDisabled = !hasSpecs || isCurrentEmb;
                
                return (
                  <button
                    key={embIndex}
                    onClick={() => onSelectEmbarcacao(embIndex)}
                    disabled={isDisabled}
                    className={`w-full p-2 rounded text-left text-sm transition-colors ${
                      selectedEmbarcacao === embIndex
                        ? 'bg-blue-100 border-blue-300 border'
                        : !isDisabled
                          ? 'bg-gray-50 hover:bg-gray-100 border border-transparent'
                          : 'bg-gray-100 opacity-50 cursor-not-allowed border border-transparent'
                    }`}
                  >
                    <p className="font-medium">
                      Embarcação #{emb.numero || embIndex + 1}
                      {isCurrentEmb && <span className="text-blue-500 ml-2">(atual)</span>}
                      {!hasSpecs && !isCurrentEmb && <span className="text-gray-400 ml-2">(vazia)</span>}
                    </p>
                    <p className="text-xs text-gray-500">{emb.vessel_model_nome || 'Sem modelo'}</p>
                    {hasSpecs && (
                      <p className="text-xs text-green-600">
                        {emb.checklist_preenchido.filter(i => i.status).length}/{emb.checklist_preenchido.length} itens
                      </p>
                    )}
                  </button>
                );
              })}
              {!selectedProject && (
                <p className="text-center text-gray-400 py-4 text-sm">
                  Selecione um projeto
                </p>
              )}
            </div>
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button 
            onClick={onImport}
            disabled={!selectedProject || selectedEmbarcacao === null || loading}
          >
            {loading ? 'Importando...' : 'Importar Ficha'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
